import React, { useCallback } from 'react'
import styled from 'styled-components'
import { ChainId, Currency, CurrencyAmount, Fraction, Percent, Token, Trade, TradeType } from '@nominex/nomiswap-sdk'
import { Tag } from '@nominex/dex-uikit'
import { SlDivider } from '@shoelace-style/shoelace/dist/react'

import { CashbackPoolResponse } from 'restApi/openapi/models'

import { LP_FEE_FRACTION, REFUNDABLE_FRACTION } from 'config/constants/exchange'
import { useTranslation } from 'contexts/Localization'
import { useUserSlippageTolerance } from 'state/user/hooks'
import { Field } from 'state/swap/actions'
import { computeSlippageAdjustedAmounts } from 'utils/prices'
import useNmxCashback from 'hooks/useNmxCashback'

import { AutoColumn, RowBetween, RowFixed } from 'components/Layout'
import QuestionHelper from 'components/QuestionHelper'

import { NMX } from 'config/tokens'

import { formatNumber } from 'utils/formatters'
import FormattedPriceImpact from './FormattedPriceImpact'
import SwapRoute from './SwapRoute'

const StyledSlDivider = styled(SlDivider)`
  --color: ${({ theme }) => `${theme.colors.darkGray300}80`};
`

const percentFormat = {
  style: 'percent' as const,
  minimumSignificantDigits: 2,
  maximumSignificantDigits: 2,
}

function TradeSummary({
  allowedSlippage,
  cashbackPool,
  trade,
  priceImpactWithoutFee,
  realizedTradeFee,
  realizedLpFee,
  tradeFee,
}: {
  allowedSlippage: number
  cashbackPool: CashbackPoolResponse
  trade: Trade<Currency, Currency, TradeType>
  priceImpactWithoutFee: Percent
  realizedTradeFee: CurrencyAmount<Token>
  realizedLpFee: Fraction
  tradeFee: Percent
}) {
  const { t } = useTranslation()
  const { nmxCashback } = useNmxCashback(trade)

  const isDirectNmxPair =
    trade.route.path.some((token) => token.equals(NMX[ChainId.BSC])) && trade.route.path.length === 2
  const isExactIn = trade.tradeType === TradeType.EXACT_INPUT
  const slippageAdjustedAmounts = computeSlippageAdjustedAmounts(trade, allowedSlippage)
  const cashbackPoolDelta = cashbackPool.total - cashbackPool.distributed

  const formattedTradeFee = formatNumber(Number(tradeFee.toFixed(8)), percentFormat)
  const formattedLpFee = formatNumber(Number(tradeFee.multiply(LP_FEE_FRACTION).toFixed(8)), percentFormat)
  const formattedNomiswapFee = formatNumber(Number(tradeFee.multiply(REFUNDABLE_FRACTION).toFixed(8)), percentFormat)

  const displayMinReceived = useCallback(
    () =>
      slippageAdjustedAmounts[isExactIn ? Field.OUTPUT : Field.INPUT]
        ? `${slippageAdjustedAmounts[isExactIn ? Field.OUTPUT : Field.INPUT].toSignificant(4)} ${
            trade[isExactIn ? 'outputAmount' : 'inputAmount'].currency.symbol
          }`
        : '-',
    [isExactIn, slippageAdjustedAmounts, trade],
  )

  const displayTradeFee = useCallback(
    () =>
      realizedTradeFee.greaterThan('0')
        ? `${realizedTradeFee.toSignificant(4)} ${trade.inputAmount.currency.symbol}`
        : '-',
    [realizedTradeFee, trade.inputAmount.currency.symbol],
  )
  const displayLpFee = useCallback(
    () =>
      realizedLpFee.greaterThan('0') ? `${realizedLpFee.toSignificant(4)} ${trade.inputAmount.currency.symbol}` : '-',
    [realizedLpFee, trade.inputAmount.currency.symbol],
  )
  const displayNmxCashback = useCallback(() => `≈${formatNumber(nmxCashback.toNumber())} NMX`, [nmxCashback])

  return (
    <AutoColumn gap="8px">
      <RowBetween>
        <RowFixed>
          <div className="text-white text-14">{isExactIn ? t('Minimum received') : t('Maximum sold')}</div>
          <QuestionHelper
            content={t(
              'Your transaction will revert if there is a large, unfavorable price movement before it is confirmed.',
            )}
          />
        </RowFixed>

        <RowFixed>
          <div className="text-white text-14 notranslate" translate="no">
            {displayMinReceived()}
          </div>
        </RowFixed>
      </RowBetween>

      <RowBetween>
        <RowFixed>
          <div className="text-white text-14">{t('Price Impact')}</div>
          <QuestionHelper
            content={t('The difference between the market price and estimated price due to trade size.')}
          />
        </RowFixed>
        <RowFixed>
          <FormattedPriceImpact priceImpact={priceImpactWithoutFee} />
        </RowFixed>
      </RowBetween>

      <RowBetween>
        <RowFixed>
          <div className="text-white text-14">{t('Trade fee')}</div>
          {isDirectNmxPair ? (
            <QuestionHelper
              content={t('There is a %tradeFee% trade fee. For each trade %tradeFee% fee goes to NMX burning.', {
                tradeFee: formattedTradeFee,
              })}
            />
          ) : (
            <QuestionHelper
              content={t(
                'There is a %tradeFee% trade fee. For each trade %lpFee% goes to liquidity providers and %nomiswapFee% goes to referral program rewards, cashback and Nomiswap profit.',
                { tradeFee: formattedTradeFee, lpFee: formattedLpFee, nomiswapFee: formattedNomiswapFee },
              )}
            />
          )}
        </RowFixed>
        <div className="text-white text-14 notranslate" translate="no">
          {displayTradeFee()}
        </div>
      </RowBetween>

      {!isDirectNmxPair && (
        <>
          <RowBetween>
            <RowFixed>
              <div className="text-white text-14">{t('Liquidity provider fee')}</div>
              <QuestionHelper
                content={t('For each trade a %lpFee% fee goes to liquidity providers', { lpFee: formattedLpFee })}
              />
            </RowFixed>
            <div className="text-white text-14 notranslate" translate="no">
              {displayLpFee()}
            </div>
          </RowBetween>
          <RowBetween>
            <RowFixed>
              <div className="text-white text-14">{t('Cashback')}</div>
              <QuestionHelper
                content={t(
                  'The amount of NMX tokens you will get from a %nomiswapFee% of trade fee after a swap. The value is estimated, and by no means represent guaranteed cashback.',
                  { nomiswapFee: formattedNomiswapFee },
                )}
              />
            </RowFixed>
            {cashbackPoolDelta <= 0 ? (
              <Tag scale="xs" variant="failure">
                <div className="text-white text-14 font-medium">{t('Pool is over')}</div>
              </Tag>
            ) : (
              <Tag scale="xs">
                <div className="text-white text-14 font-medium notranslate" translate="no">
                  {displayNmxCashback()}
                </div>
              </Tag>
            )}
          </RowBetween>
        </>
      )}
    </AutoColumn>
  )
}

export interface AdvancedSwapDetailsProps {
  trade?: Trade<Currency, Currency, TradeType>
  cashbackPool: CashbackPoolResponse
  priceImpactWithoutFee: Percent
  realizedTradeFee: CurrencyAmount<Token>
  realizedLpFee: Fraction
  tradeFee: Percent
}

export function AdvancedSwapDetails({
  trade,
  cashbackPool,
  priceImpactWithoutFee,
  realizedTradeFee,
  realizedLpFee,
  tradeFee,
}: AdvancedSwapDetailsProps) {
  const { t } = useTranslation()
  const [allowedSlippage] = useUserSlippageTolerance()

  const showRoute = Boolean(trade && trade.route.path.length > 2)

  return (
    <AutoColumn>
      <>
        <TradeSummary
          allowedSlippage={allowedSlippage}
          cashbackPool={cashbackPool}
          trade={trade}
          priceImpactWithoutFee={priceImpactWithoutFee}
          realizedTradeFee={realizedTradeFee}
          realizedLpFee={realizedLpFee}
          tradeFee={tradeFee}
        />
        {showRoute && (
          <>
            <StyledSlDivider />

            <RowBetween>
              <div className="flex items-center">
                <div className="text-white text-14">{t('Route')}</div>
                <QuestionHelper
                  content={t('Routing through these tokens resulted in the best price for your trade.')}
                />
              </div>
              <SwapRoute trade={trade} />
            </RowBetween>
          </>
        )}
      </>
    </AutoColumn>
  )
}
